/**
 * Construtor genérico de tabelas para a planilha Univer.
 * Escalável: basta criar novas definições de tabela e passar para composeTables.
 */
export const buildTable = ({
  title,
  startRow = 0,
  startCol = 0,
  columns = [],
  data = [],
  totals = [],
  mergeCells = [],
  titleStyle = "sectionTitle",
  headerStyle = "header",
  emptyValue = "",
  titleColSpan = null,
}) => {
  const cellData = {};
  const columnData = {};
  const mergedCells = [];
  let currentRow = startRow;

  // === 1. Título da seção ===
  if (title) {
    const colSpan = titleColSpan ?? columns.length;
    cellData[currentRow] = {
      [startCol]: { v: title, s: titleStyle },
    };
    mergedCells.push({
      startRow: currentRow,
      startCol,
      endRow: currentRow,
      endCol: startCol + colSpan - 1,
    });
    currentRow++;
  }

  // === 2. Cabeçalho ===
  cellData[currentRow] = {};
  columns.forEach((col, colIndex) => {
    const absoluteCol = startCol + colIndex;
    cellData[currentRow][absoluteCol] = { v: col.label, s: headerStyle };
    if (col.width) columnData[absoluteCol] = { w: col.width };
  });
  currentRow++;

  // === 3. Linhas de dados ===
  const dataStartRow = currentRow;

  data.forEach((item, rowIndex) => {
    const absoluteRow = currentRow + rowIndex;
    cellData[absoluteRow] = {};

    columns.forEach((col, colIndex) => {
      const absoluteCol = startCol + colIndex;
      const cell = {};

      // Valor ou fórmula
      if (col.formula) {
        cell.f = col.formula.replace(/\{row\}/g, absoluteRow + 1);
      } else if (col.valueFn) {
        cell.v = col.valueFn(item, rowIndex);
      } else if (col.key) {
        cell.v = item[col.key] ?? emptyValue;
      }

      // Tipo da célula (crucial para datas: type = 2 / CellValueType.NUMBER)
      if (col.type !== undefined) {
        cell.t = col.type;
      }

      // Estilo
      if (col.styleFn) {
        cell.s = col.styleFn(item, rowIndex);
      } else if (col.style) {
        cell.s = col.style;
      }

      cell.locked = col.locked === true;

      // Formato inline (override do estilo, caso necessário)
      if (col.format) {
        cell.n = col.format;
      }

      cellData[absoluteRow][absoluteCol] = cell;
    });
  });

  currentRow += data.length;

  // === 4. Linha de totais ===
  if (totals.length > 0) {
    cellData[currentRow] = {};

    totals.forEach((total) => {
      const absoluteCol = startCol + (total.colIndex ?? 0);
      const cell = {};

      if (total.formula) {
        cell.f = total.formula
          .replace(/\{dataStart\}/g, dataStartRow + 1)
          .replace(/\{dataEnd\}/g, dataStartRow + data.length)
          .replace(/\{row\}/g, currentRow + 1);
      } else if (total.value !== undefined) {
        cell.v = total.value;
      }

      if (total.style) cell.s = total.style;

      if (total.label && total.labelColIndex !== undefined) {
        cellData[currentRow][startCol + total.labelColIndex] = {
          v: total.label,
          s: total.labelStyle || "totalLabel",
        };
      }

      cellData[currentRow][absoluteCol] = cell;
    });

    currentRow++;
  }

  // === 5. Merges customizados da definição ===
  mergeCells.forEach((merge) => {
    mergedCells.push({
      startRow: startRow + merge.startRow,
      startCol: startCol + merge.startCol,
      endRow: startRow + merge.endRow,
      endCol: startCol + merge.endCol,
    });
  });

  // === 6. Linha vazia de separação ===
  currentRow++;

  return {
    cellData,
    columnData,
    mergedCells,
    nextRow: currentRow,
    columnCount: columns.length,
    dataStartRow,
    dataEndRow: currentRow - 2,
  };
};

/**
 * Combina múltiplas tabelas em um único cellData/columnData.
 * Uso: composeTables([{ definition, data, startCol? }, ...])
 */
export const composeTables = (tables) => {
  const mergedCellData = {};
  const mergedColumnData = {};
  const allMergedCells = [];
  let currentRow = 0;
  let maxColumn = 0;

  tables.forEach((table) => {
    const result = buildTable({
      ...table.definition,
      startRow: table.startRow ?? currentRow,
      startCol: table.startCol ?? 0,
      data: table.data,
    });

    Object.entries(result.cellData).forEach(([row, cols]) => {
      mergedCellData[row] = { ...(mergedCellData[row] || {}), ...cols };
    });
    Object.entries(result.columnData).forEach(([col, config]) => {
      mergedColumnData[col] = config;
    });

    allMergedCells.push(...result.mergedCells);
    currentRow = result.nextRow; // continua daqui pra tabela seguinte SEM posição explícita
    maxColumn = Math.max(maxColumn, (table.startCol ?? 0) + result.columnCount);
  });

  return {
    cellData: mergedCellData,
    columnData: mergedColumnData,
    mergedCells: allMergedCells,
    totalRows: currentRow,
    totalColumns: maxColumn,
  };
};

/**
 * Calcula quantas linhas uma tabela vai ocupar, sem montar cellData.
 * Use para posicionar a próxima tabela de forma determinística
 * (ao invés de "+5" no chute).
 */
export const getTableHeight = (definition, data = []) => {
  const { title, totals = [] } = definition;
  let rows = 1; // header
  if (title) rows += 1;
  rows += data.length;
  if (totals.length > 0) rows += 1;
  rows += 1; // linha vazia de separação
  return rows;
};
